/**
 * 鍵階層オーケストレータ (KEY_HIERARCHY.md §4)。
 *
 * C-1〜C-6 の低レベル関数を組み合わせて、登録・ログインの 3 つの高レベル操作を提供する:
 *
 *   prepareRegistration  — POST /api/auth/register 用ペイロードを構築 (§4.1)
 *   prepareLogin         — prelogin レスポンスから POST /api/auth/login 用コンテキストを構築 (§4.2 step 1-4)
 *   completeLogin        — login レスポンスから M2 検証 + SK 復元 (§4.2 step 5-7)
 *
 * セキュリティ原則:
 *   - Master Password (MP) はこのモジュールの引数に入るが、返値に含めない
 *   - MK / SMK_enc / SRP x は関数スコープ内でのみ存在し、withZeroize で破棄する
 *   - Argon2id は必ず deriveMasterKeyInWorker 経由 (メインスレッドブロック禁止)
 *   - I/O (fetch) は持たない — pure crypto only
 *   - console.log / 永続化なし
 */
import { deriveMasterKeyInWorker } from './argon2-worker-client'
import { aesGcmSeal, aesGcmOpen } from './aes-gcm'
import { deriveStretchedMasterKeyEnc, hkdfSha256 } from './hkdf'
import { randomKeyBytes, randomIvBytes, randomSaltBytes, randomBytes } from './random'
import { bytesToHex, bytesToBase64, base64ToBytes, hexToBytes, utf8ToBytes } from './encoding'
import { withZeroize } from './zeroize'
import {
  generateSrpSalt,
  deriveSrpPrivateKey,
  deriveSrpVerifier,
  generateClientEphemeral,
  deriveClientSession,
  verifyServerProof,
} from './srp-client'
import { ARGON2, HKDF, RECOVERY } from './constants'
import { deriveRecoverySeed, generateRecoveryMnemonic } from '../features/auth/services/recoveryKey'
import type {
  PreloginResponse,
  LoginSuccessResponse,
  LoginRequest,
  RegisterRequest,
  LoginContext,
  CompleteLoginResult,
  PrepareRegistrationResult,
  PrepareRecoveryResetInput,
  PrepareMasterPasswordChangeInput,
  ChangeMasterPasswordRequest,
  PrepareRecoveryKeyRotationInput,
  PrepareRecoveryKeyRotationResult,
  RecoveryKeyArtifacts,
  RecoveryResetRequest,
  TwoFactorLoginVerifyResponse,
} from './key-hierarchy.types'

// ── generateRecoveryKeyArtifacts ────────────────────────────────────────────

/**
 * Recovery Key 並列封印素材を生成する (KEY_HIERARCHY.md §4.7、register / rotate 共用)。
 *
 * 入力: SK (32 byte、不変、呼び出し側が所有権を持つので本関数は zeroize しない)
 * 出力: { wire: 5 列 (verifier / protectedSK / IV / kdfSalt / kdfVersion), mnemonic: 24 単語 }
 *
 * Step:
 *  1. RM = generateRecoveryMnemonic() — BIP39 24 単語
 *  2. RS = mnemonicToSeed(RM, "") — PBKDF2-HMAC-SHA512(c=2048)
 *  3. recoveryKdfSalt = randomBytes(16)
 *  4. KEK_recovery = HKDF-SHA256(RS, salt, info="OoKey/recovery/kek/v1", L=32)
 *  5. RV           = HKDF-SHA256(RS, salt, info="OoKey/recovery/verify/v1", L=32)
 *  6. recoveryIv   = randomIvBytes()
 *  7. recoveryProtectedSymmetricKey = aesGcmSeal(KEK_recovery, recoveryIv, SK)
 *
 * RS / KEK_recovery / RV は withZeroize で派生スコープ外に出さない。`mnemonic` は
 * caller (RegisterPage / RotateRecoveryKeySection) が一回限り表示する。
 */
export const generateRecoveryKeyArtifacts = async (
  symmetricKey: Uint8Array,
): Promise<{ wire: RecoveryKeyArtifacts; mnemonic: string }> => {
  const mnemonic = generateRecoveryMnemonic()
  const recoverySeed = await deriveRecoverySeed(mnemonic)

  const wire = await withZeroize([recoverySeed], async () => {
    const recoveryKdfSalt = randomBytes(RECOVERY.KDF_SALT_BYTES)

    const kekRecovery = await hkdfSha256(
      recoverySeed,
      utf8ToBytes(HKDF.INFO_RECOVERY_KEK),
      RECOVERY.HKDF_OUTPUT_BYTES,
      recoveryKdfSalt,
    )

    return withZeroize([kekRecovery], async () => {
      const recoveryVerifier = await hkdfSha256(
        recoverySeed,
        utf8ToBytes(HKDF.INFO_RECOVERY_VERIFY),
        RECOVERY.HKDF_OUTPUT_BYTES,
        recoveryKdfSalt,
      )

      return withZeroize([recoveryVerifier], async () => {
        const recoveryIv = randomIvBytes()
        const recoveryProtectedSk = await aesGcmSeal(kekRecovery, recoveryIv, symmetricKey)

        return {
          recoveryKeyVerifier: bytesToHex(recoveryVerifier),
          recoveryProtectedSymmetricKey: bytesToBase64(recoveryProtectedSk),
          recoveryProtectedSymmetricKeyIv: bytesToBase64(recoveryIv),
          recoveryKdfSalt: bytesToHex(recoveryKdfSalt),
          recoveryKdfVersion: RECOVERY.KDF_VERSION,
        }
      })
    })
  })

  return { wire, mnemonic }
}

// ── prepareRegistration ─────────────────────────────────────────────────────

/**
 * アカウント登録用ペイロードを構築する (KEY_HIERARCHY.md §4.1 + §4.7 Recovery)。
 *
 * Step (通常 MP 経路):
 *   1. MP = password (引数)
 *   2. kdfSalt = randomSaltBytes()
 *   3. MK = Argon2id(MP, kdfSalt) ← Worker で実行
 *   4. SMK_enc = HKDF(MK, "enc")
 *   5. SK = randomKeyBytes()
 *   6. iv = randomIvBytes()
 *   7. protectedSymmetricKey = aesGcmSeal(SMK_enc, iv, SK)
 *   8. srpSalt = generateSrpSalt()
 *   9. x = deriveSrpPrivateKey(srpSalt, email, MP)
 *  10. v = deriveSrpVerifier(x)
 *
 * Step (Recovery Key 並列封印、KEY_HIERARCHY.md §4.7):
 *  11. RM = generateRecoveryMnemonic() — BIP39 24 単語
 *  12. RS = mnemonicToSeed(RM, "") — PBKDF2-HMAC-SHA512(c=2048)
 *  13. recoveryKdfSalt = randomBytes(16)
 *  14. KEK_recovery = HKDF-SHA256(RS, salt=recoveryKdfSalt, info="OoKey/recovery/kek/v1", L=32)
 *  15. RV           = HKDF-SHA256(RS, salt=recoveryKdfSalt, info="OoKey/recovery/verify/v1", L=32)
 *  16. recoveryIv   = randomIvBytes()
 *  17. recoveryProtectedSymmetricKey = aesGcmSeal(KEK_recovery, recoveryIv, SK)
 *  → { request: RegisterRequest, recoveryMnemonic: string }
 *
 * MP は呼び出し後に GC 可能なスコープに限定すること。
 * MK / SMK_enc / SK / RS / KEK_recovery / RV は withZeroize で派生スコープ外に出さない。
 * `recoveryMnemonic` はサーバには送信せず、caller (RegisterPage) が一回限り表示する。
 */
export const prepareRegistration = async (input: {
  email: string
  name?: string
  password: string
  masterPasswordHint?: string
}): Promise<PrepareRegistrationResult> => {
  const { email, name, password, masterPasswordHint } = input

  // step 2: kdfSalt (16 byte)
  const kdfSalt = randomSaltBytes()

  // step 3: MK = Argon2id(MP, kdfSalt) — Worker で実行
  const mk = await deriveMasterKeyInWorker(password, kdfSalt)

  return withZeroize([mk], async () => {
    // step 4: SMK_enc = HKDF(MK, "enc", 32)
    const smkEnc = await deriveStretchedMasterKeyEnc(mk)

    return withZeroize([smkEnc], async () => {
      // step 5-6: SK, iv
      const sk = randomKeyBytes()
      const iv = randomIvBytes()

      return withZeroize([sk], async () => {
        // step 7: protectedSymmetricKey = AES-GCM(SMK_enc, iv, SK)
        const protectedSk = await aesGcmSeal(smkEnc, iv, sk)

        // step 8-10: SRP
        const srpSalt = generateSrpSalt()
        const x = deriveSrpPrivateKey(srpSalt, email, password)
        const v = deriveSrpVerifier(x)

        // step 11-17: Recovery Key 並列封印 (KEY_HIERARCHY.md §4.7、`generateRecoveryKeyArtifacts` ヘルパー共用)
        const { wire: recovery, mnemonic } = await generateRecoveryKeyArtifacts(sk)

        const request: RegisterRequest = {
          email,
          ...(name !== undefined ? { name } : {}),
          srpSalt,
          srpVerifier: v,
          kdfMemoryKib: ARGON2.DEFAULT_MEMORY_KIB,
          kdfTimeCost: ARGON2.DEFAULT_TIME_COST,
          kdfParallelism: ARGON2.DEFAULT_PARALLELISM,
          kdfSalt: bytesToHex(kdfSalt),
          protectedSymmetricKey: bytesToBase64(protectedSk),
          protectedSymmetricKeyIv: bytesToBase64(iv),
          ...(masterPasswordHint !== undefined ? { masterPasswordHint } : {}),
          ...recovery,
        }

        return { request, recoveryMnemonic: mnemonic }
      })
    })
  })
}

// ── prepareLogin ────────────────────────────────────────────────────────────

/**
 * prelogin レスポンスを使ってログイン用コンテキストを構築する (KEY_HIERARCHY.md §4.2 step 1-4)。
 *
 * Step:
 *   1. MK = Argon2id(MP, prelogin.kdf.salt, prelogin.kdf params) ← Worker で実行
 *   2. x = deriveSrpPrivateKey(prelogin.salt, email, MP)
 *   3. (a, A) = generateClientEphemeral()
 *   4. clientSession = deriveClientSession(a, B, prelogin.salt, email, x) → { key: K, proof: M1 }
 *   → LoginContext
 *
 * _smkEnc は completeLogin() が SK を unwrap するまで保持する。
 * completeLogin() が呼ばれた後に withZeroize で破棄される。
 */
export const prepareLogin = async (
  email: string,
  password: string,
  prelogin: PreloginResponse,
): Promise<LoginContext> => {
  // step 1: MK = Argon2id(MP, kdfSalt, prelogin kdf params)
  const kdfSalt = hexToBytes(prelogin.kdf.salt)

  const mk = await deriveMasterKeyInWorker(password, kdfSalt, {
    memoryCost: prelogin.kdf.memoryCost,
    timeCost: prelogin.kdf.timeCost,
    parallelism: prelogin.kdf.parallelism,
  })

  return withZeroize([mk], async () => {
    // step 4-cont: SMK_enc を先に派生して ctx に保持 (step 5-7 で使用)
    const smkEnc = await deriveStretchedMasterKeyEnc(mk)
    let contextCreated = false

    try {
      // step 2: x = H(srpSalt, email, MP)
      const x = deriveSrpPrivateKey(prelogin.salt, email, password)

      // step 3: (a, A) ephemeral
      const ephemeral = generateClientEphemeral()

      // step 4: clientSession = deriveSession(a, B, salt, email, x)
      const session = deriveClientSession(
        ephemeral.secret,
        prelogin.serverPublicEphemeral,
        prelogin.salt,
        email,
        x,
      )

      const request: LoginRequest = {
        email,
        sessionId: prelogin.sessionId,
        clientPublicEphemeral: ephemeral.public,
        clientSessionProof: session.proof,
      }

      const context = createLoginContext(request, session, smkEnc)

      contextCreated = true

      return context
    } finally {
      if (!contextCreated) {
        smkEnc.fill(0)
      }
    }
  })
}

const createLoginContext = (
  request: LoginRequest,
  session: LoginContext['_session'],
  smkEnc: Uint8Array,
): LoginContext => {
  const context = { request } as LoginContext

  Object.defineProperties(context, {
    _session: {
      value: session,
      enumerable: false,
    },
    _smkEnc: {
      value: smkEnc,
      enumerable: false,
    },
  })

  return context
}

// ── completeLogin ───────────────────────────────────────────────────────────

/**
 * SRP M2 (相互認証) のみを検証する。`_smkEnc` は zeroize しない。
 *
 * 2FA pending path で「protectedSymmetricKey はまだ来ていないが M2 だけ先に検証」
 * したい場合に使う。検証失敗時は SrpVerificationError を throw。
 */
export const verifyLoginProof = (ctx: LoginContext, serverSessionProof: string): void => {
  verifyServerProof(ctx.request.clientPublicEphemeral, ctx._session, serverSessionProof)
}

/**
 * `_smkEnc` で protected SK を AES-GCM-Open し、SK を取り出す。終了時に zeroize する。
 *
 * 通常 login path (login response の SK)、および 2FA login-verify の SK 受領後、
 * どちらからも呼ばれる共通ヘルパ。
 */
export const unwrapSymmetricKey = async (
  ctx: LoginContext,
  protectedSymmetricKey: string,
  protectedSymmetricKeyIv: string,
): Promise<Uint8Array> => {
  return withZeroize([ctx._smkEnc], async () => {
    const iv = base64ToBytes(protectedSymmetricKeyIv)
    const sealedSk = base64ToBytes(protectedSymmetricKey)

    return aesGcmOpen(ctx._smkEnc, iv, sealedSk)
  })
}

/**
 * 通常 login path: M2 検証 + SK 復元を一括で行う (KEY_HIERARCHY.md §4.2 step 5-7)。
 *
 * Step:
 *   5. verifyServerProof(A, _session, M2) ← 失敗で SrpVerificationError
 *   6. SMK_enc = ctx._smkEnc (prepareLogin で保持)
 *   7. SK = aesGcmOpen(SMK_enc, iv, protectedSymmetricKey)
 *   → { symmetricKey: SK, accessToken }
 *
 * ctx._smkEnc は `unwrapSymmetricKey` 内の withZeroize で必ず zeroize される。
 */
export const completeLogin = async (
  ctx: LoginContext,
  login: LoginSuccessResponse,
): Promise<CompleteLoginResult> => {
  verifyLoginProof(ctx, login.serverSessionProof)

  const symmetricKey = await unwrapSymmetricKey(
    ctx,
    login.protectedSymmetricKey,
    login.protectedSymmetricKeyIv,
  )

  return { symmetricKey, accessToken: login.accessToken }
}

/**
 * 2FA login-verify path: 既に M2 検証済 (loginFlow で先行実施) の前提で、
 * login-verify レスポンスから SK を復元する。
 *
 * ctx._smkEnc は `unwrapSymmetricKey` 内で zeroize される。
 */
export const completeTwoFactorLogin = async (
  ctx: LoginContext,
  res: TwoFactorLoginVerifyResponse,
): Promise<CompleteLoginResult> => {
  const symmetricKey = await unwrapSymmetricKey(
    ctx,
    res.protectedSymmetricKey,
    res.protectedSymmetricKeyIv,
  )

  return { symmetricKey, accessToken: res.accessToken }
}

// ── prepareRecoveryReset (T-C5e、KEY_HIERARCHY.md §4.7.2) ───────────────────

/**
 * Recovery Key 経路の reset 用 wire payload を構築する (KEY_HIERARCHY.md §4.7.2)。
 *
 * 入力: 新 MP, email, 既に Recovery Key 経路で復元済の SK (32 byte)
 * 出力: PUT /auth/recovery/reset の body 相当 (新 SRP / KDF / protectedSymmetricKey 一式)
 *
 * Step:
 *   1. kdfSalt_new = randomSaltBytes()
 *   2. MK_new      = Argon2id(MP_new, kdfSalt_new) ← Worker
 *   3. SMK_enc_new = HKDF(MK_new, "ookey/v1/smk-enc", 32)
 *   4. iv_new      = randomIvBytes()
 *   5. protectedSymmetricKey_new = aesGcmSeal(SMK_enc_new, iv_new, SK_existing)
 *   6. srpSalt_new = generateSrpSalt()
 *   7. x_new       = deriveSrpPrivateKey(srpSalt_new, email, MP_new)
 *   8. v_new       = deriveSrpVerifier(x_new)
 *
 * セキュリティ (CLAUDE.md §2 / KEY_HIERARCHY.md §4.7):
 *   - SK は引数で受け取り、再封印時のみ参照、本関数は zeroize しない
 *     (caller = recoveryContext が clear() で zeroize する責任)
 *   - MK_new / SMK_enc_new は withZeroize で派生スコープ外に出さない
 *   - 平文 MP_new はサーバに送信しない (verifier 派生にのみ使用)
 *   - Recovery 5 列 (recoveryKeyVerifier 等) は **更新しない**: 既存 mnemonic で
 *     再ログイン後の reset でも引き続き使えるよう、recoveryProtectedSymmetricKey
 *     も触らない (SK 自体が不変なら再封印不要、KEY_HIERARCHY.md §4.7.2)
 */
export const prepareRecoveryReset = async (
  input: PrepareRecoveryResetInput,
): Promise<RecoveryResetRequest> => {
  const { email, newPassword, symmetricKey } = input

  // step 1: 新 kdfSalt (16 byte)
  const kdfSaltNew = randomSaltBytes()

  // step 2: 新 MK = Argon2id(MP_new, kdfSalt_new) — Worker
  const mkNew = await deriveMasterKeyInWorker(newPassword, kdfSaltNew)

  return withZeroize([mkNew], async () => {
    // step 3: SMK_enc_new = HKDF(MK_new, "enc", 32)
    const smkEncNew = await deriveStretchedMasterKeyEnc(mkNew)

    return withZeroize([smkEncNew], async () => {
      // step 4-5: iv_new, protectedSymmetricKey_new = AES-GCM(SMK_enc_new, iv_new, SK)
      const ivNew = randomIvBytes()
      const protectedSkNew = await aesGcmSeal(smkEncNew, ivNew, symmetricKey)

      // step 6-8: 新 SRP
      const srpSaltNew = generateSrpSalt()
      const xNew = deriveSrpPrivateKey(srpSaltNew, email, newPassword)
      const vNew = deriveSrpVerifier(xNew)

      const request: RecoveryResetRequest = {
        srpSalt: srpSaltNew,
        srpVerifier: vNew,
        kdfMemoryKib: ARGON2.DEFAULT_MEMORY_KIB,
        kdfTimeCost: ARGON2.DEFAULT_TIME_COST,
        kdfParallelism: ARGON2.DEFAULT_PARALLELISM,
        kdfSalt: bytesToHex(kdfSaltNew),
        protectedSymmetricKey: bytesToBase64(protectedSkNew),
        protectedSymmetricKeyIv: bytesToBase64(ivNew),
      }

      return request
    })
  })
}

// ── prepareMasterPasswordChange (Track E-1) ─────────────────────────────────

/**
 * マスターパスワード変更用 wire payload を構築する (Track E-1)。
 *
 * 入力: 旧 MP + 新 MP + email + 現セッション SK + 現 MP に紐づく prelogin response
 * 出力: PUT /api/users/me/master-password の body 相当
 *
 * Step:
 *   1. **旧 MP 知識証明** (Argon2 不要、SHA-1 ベース SRP のみ):
 *      a. x_old = deriveSrpPrivateKey(prelogin.salt, email, oldPassword)
 *      b. (a, A) = generateClientEphemeral()
 *      c. clientSession = deriveClientSession(a, B, salt, email, x_old) → M1
 *   2. **新 MP 派生** (Argon2 1 回 + HKDF + AES-GCM):
 *      d. kdfSalt_new = randomSaltBytes()
 *      e. MK_new = Argon2id(MP_new, kdfSalt_new) ← Worker
 *      f. SMK_enc_new = HKDF(MK_new, "enc", 32)
 *      g. iv_new = randomIvBytes()
 *      h. protectedSymmetricKey_new = aesGcmSeal(SMK_enc_new, iv_new, SK)
 *      i. srpSalt_new = generateSrpSalt()
 *      j. x_new = deriveSrpPrivateKey(srpSalt_new, email, MP_new)
 *      k. v_new = deriveSrpVerifier(x_new)
 *
 * セキュリティ (CLAUDE.md §2 / KEY_HIERARCHY.md §4.7.2):
 *   - 平文 MP_old / MP_new はサーバに送信しない (SRP M1 + verifier + protectedSK のみ)
 *   - SK は引数で受け取り、新 SMK_enc で再封印するのみ。本関数は zeroize しない
 *     (caller = sessionStore が clear() で zeroize する責任)
 *   - MK_new / SMK_enc_new は withZeroize で派生スコープ外に出さない
 *   - Recovery 5 列 (recoveryKeyVerifier 等) は **更新しない**: SK 不変のため
 *     既存 mnemonic は引き続き有効 (KEY_HIERARCHY.md §4.7.2)
 *   - **旧 MP の Argon2 は不要**: SRP は `deriveSrpPrivateKey` (SHA-1 ベース) のみ
 *     使用し、MK_old を経由しない。新 MP の Argon2 1 回だけ走る (~1-3 秒)
 */
export const prepareMasterPasswordChange = async (
  input: PrepareMasterPasswordChangeInput,
): Promise<ChangeMasterPasswordRequest> => {
  const { email, oldPassword, newPassword, symmetricKey, prelogin } = input

  // ── Step 1: 旧 MP 知識証明 (SRP M1、Argon2 不要) ──
  const xOld = deriveSrpPrivateKey(prelogin.salt, email, oldPassword)
  const ephemeral = generateClientEphemeral()

  const session = deriveClientSession(
    ephemeral.secret,
    prelogin.serverPublicEphemeral,
    prelogin.salt,
    email,
    xOld,
  )

  // ── Step 2: 新 MP 派生 (Argon2 + HKDF + AES-GCM + SRP verifier) ──
  const kdfSaltNew = randomSaltBytes()
  const mkNew = await deriveMasterKeyInWorker(newPassword, kdfSaltNew)

  return withZeroize([mkNew], async () => {
    const smkEncNew = await deriveStretchedMasterKeyEnc(mkNew)

    return withZeroize([smkEncNew], async () => {
      const ivNew = randomIvBytes()
      const protectedSkNew = await aesGcmSeal(smkEncNew, ivNew, symmetricKey)

      const srpSaltNew = generateSrpSalt()
      const xNew = deriveSrpPrivateKey(srpSaltNew, email, newPassword)
      const vNew = deriveSrpVerifier(xNew)

      const request: ChangeMasterPasswordRequest = {
        // SRP proof for 旧 MP
        sessionId: prelogin.sessionId,
        clientPublicEphemeral: ephemeral.public,
        clientSessionProof: session.proof,
        // 新 SRP / KDF / protectedSymmetricKey
        srpSalt: srpSaltNew,
        srpVerifier: vNew,
        kdfMemoryKib: ARGON2.DEFAULT_MEMORY_KIB,
        kdfTimeCost: ARGON2.DEFAULT_TIME_COST,
        kdfParallelism: ARGON2.DEFAULT_PARALLELISM,
        kdfSalt: bytesToHex(kdfSaltNew),
        protectedSymmetricKey: bytesToBase64(protectedSkNew),
        protectedSymmetricKeyIv: bytesToBase64(ivNew),
      }

      return request
    })
  })
}

// ── prepareRecoveryKeyRotation (Track E-2) ──────────────────────────────────

/**
 * Recovery Key 再生成用ペイロードを構築する (KEY_HIERARCHY.md §4.7.2)。
 *
 * Step:
 *  1. `generateRecoveryKeyArtifacts(SK)` で新 24 単語 + 5 列を一括生成
 *  2. wire を `RotateRecoveryKeyRequest` として、`mnemonic` を caller に返す
 *
 * 仕様:
 *   - SK は **不変** (新 KEK_recovery で再封印するだけ)。Vault Item / Send / wrappedLinkKey は
 *     引き続き有効
 *   - SRP / KDF / protectedSymmetricKey も触らない (master-password 変更とは異なる経路)
 *   - 旧 mnemonic は backend 側で recovery 5 列が上書きされた時点で即座に invalidate
 *
 * 呼び出し側は sessionStore が `'unlocked'` 状態であることを保証してから本関数を呼ぶこと
 * (SK へのアクセス権 + 認証済み access token が必要)。
 */
export const prepareRecoveryKeyRotation = async (
  input: PrepareRecoveryKeyRotationInput,
): Promise<PrepareRecoveryKeyRotationResult> => {
  const { wire, mnemonic } = await generateRecoveryKeyArtifacts(input.symmetricKey)

  return { request: wire, recoveryMnemonic: mnemonic }
}
